import { AlertCircle } from "lucide-react";
import { MediaAssetCard } from "./MediaAssetCard";
import { MediaPreviewModal } from "./MediaPreviewModal";
import { useAdminMedia } from "./useAdminMedia";
import { Button } from "../ui/button";
import { Trash2, HardDrive, CheckSquare, Square, RefreshCw } from "lucide-react";

interface MediaAsset {
  key: string;
  type: "image" | "raw";
  url: string;
  thumbnailUrl: string | null;
  size: number;
}

interface MediaHeaderProps {
  loading: boolean;
  onRefresh: () => void;
  mediaListLength: number;
  selectedKeysLength: number;
  onSelectAll: () => void;
  onDeleteSelected: () => void;
  deleting: boolean;
}

function MediaHeader({
  loading,
  onRefresh,
  mediaListLength,
  selectedKeysLength,
  onSelectAll,
  onDeleteSelected,
  deleting
}: MediaHeaderProps) {
  return (
    <div className="flex flex-col md:flex-row justify-between items-start md:items-center pb-2 border-b border-border/80 gap-4">
      <div>
        <h4 className="text-xl font-bold text-foreground uppercase tracking-wider flex items-center gap-2">
          <HardDrive className="h-5 w-5 text-brand-primary" />
          <span>Media Library</span>
        </h4>
        <p className="text-xs text-muted-foreground mt-0.5">Manage Cloudflare R2 files and custom images</p>
      </div>

      <div className="flex gap-2">
        <Button type="button" variant="outline" onClick={onRefresh} disabled={loading} className="border-border/60 hover:bg-muted/50">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
        {mediaListLength > 0 && (
          <Button type="button" variant="outline" onClick={onSelectAll} className="border-border/60 hover:bg-muted/50">
            {selectedKeysLength === mediaListLength ? <Square className="h-4 w-4 mr-2" /> : <CheckSquare className="h-4 w-4 mr-2" />}
            {selectedKeysLength === mediaListLength ? "Deselect All" : "Select All"}
          </Button>
        )}
        {selectedKeysLength > 0 && (
          <Button type="button" variant="destructive" onClick={onDeleteSelected} disabled={deleting} className="bg-destructive hover:bg-destructive/95 text-white">
            <Trash2 className="h-4 w-4 mr-2" /> Delete ({selectedKeysLength})
          </Button>
        )}
      </div>
    </div>
  );
}

interface MediaGridProps {
  loading: boolean;
  mediaList: MediaAsset[];
  selectedKeys: string[];
  toggleSelect: (key: string) => void;
  setPreviewAsset: (asset: MediaAsset) => void;
  handleDeleteSingle: (key: string) => void;
}

function MediaGrid({
  loading,
  mediaList,
  selectedKeys,
  toggleSelect,
  setPreviewAsset,
  handleDeleteSingle
}: MediaGridProps) {
  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <RefreshCw className="h-8 w-8 text-brand-primary animate-spin" />
      </div>
    );
  }

  if (mediaList.length === 0) {
    return (
      <div className="text-center py-20 border border-dashed border-border/60 rounded-2xl bg-card/40">
        <p className="text-sm font-semibold text-muted-foreground">No media files found in R2 Storage.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
      {mediaList.map((asset) => (
        <MediaAssetCard
          key={asset.key}
          asset={asset}
          isSelected={selectedKeys.includes(asset.key)}
          onToggle={toggleSelect}
          onPreview={setPreviewAsset}
          onDelete={handleDeleteSingle}
        />
      ))}
    </div>
  );
}

export function AdminMediaTab() {
  const {
    mediaList,
    loading,
    selectedKeys,
    deleting,
    previewAsset,
    setPreviewAsset,
    fetchMedia,
    toggleSelect,
    selectAll,
    handleDeleteSelected,
    handleDeleteSingle
  } = useAdminMedia();

  return (
    <div className="space-y-6 text-left">
      <MediaHeader
        loading={loading}
        onRefresh={fetchMedia}
        mediaListLength={mediaList.length}
        selectedKeysLength={selectedKeys.length}
        onSelectAll={selectAll}
        onDeleteSelected={handleDeleteSelected}
        deleting={deleting}
      />

      <div className="flex items-start gap-2.5 p-3 rounded-xl bg-amber-500/5 border border-amber-500/10 text-amber-600 dark:text-amber-500 text-xs max-w-3xl leading-relaxed">
        <AlertCircle className="h-4.5 w-4.5 shrink-0 mt-0.5" />
        <div>
          <strong>Storage Management:</strong> Grouped variants are represented as single items. Deleting an image will clean up all variants automatically.
        </div>
      </div>

      <MediaGrid
        loading={loading}
        mediaList={mediaList}
        selectedKeys={selectedKeys}
        toggleSelect={toggleSelect}
        setPreviewAsset={setPreviewAsset}
        handleDeleteSingle={handleDeleteSingle}
      />

      {previewAsset && (
        <MediaPreviewModal
          asset={previewAsset}
          onClose={() => setPreviewAsset(null)}
          onDelete={(key) => {
            handleDeleteSingle(key);
            setPreviewAsset(null);
          }}
        />
      )}
    </div>
  );
}

export default AdminMediaTab;
